import { MaxbrainImage } from 'app/library/image-library/models/maxbrain-image.model';
import { Posting } from 'app/library/posting/models/posting.model';
import { CourseOfferingRecommendationComponentAlignment } from '../enums/course-offering-recommendation-component-alignment.enum';
import { CourseOfferingRecommendationComponentType } from '../enums/course-offering-recommendation-component-type.enum';
import { ICourseOfferingRecommendationItemQuery } from '../interfaces/course-offering-recommendation-item.query.interface';

// @dynamic
export class CourseOfferingRecommendationItem {
    id: string;
    type: CourseOfferingRecommendationComponentType;
    title: string | null;
    description: string | null;
    sequence: number;
    isLinked: boolean | null;
    posting: Posting | null;
    image: MaxbrainImage | null;
    alignment: CourseOfferingRecommendationComponentAlignment;

    constructor(query: ICourseOfferingRecommendationItemQuery) {
        this.id = query.id.toString();
        this.type = query.type;
        this.title = query.title;
        this.description = query.description;
        this.sequence = query.sequence;
        this.isLinked = query.is_linked;
        // API returns a partial `posting` object (without id) for non-posting recommendation items;
        // `new Posting(...)` then throws on id.toString() and aborts the whole section parse.
        this.posting = query.posting && query.posting.id != null ? new Posting(query.posting) : null;
        this.image = query.image ? new MaxbrainImage(query.image) : null;
        this.alignment = this.resolveAlignment(query.alignment);
    }

    get isPostingUnavailable(): boolean {
        if (!this.isLinked) {
            return false;
        }

        if (!this.posting) {
            return true;
        }

        if (!this.posting.validTo) {
            return false;
        }

        return this.posting.validTo.getTime() < Date.now();
    }

    get hasTitleContent(): boolean {
        return !!this.title?.trim();
    }

    get hasDescriptionContent(): boolean {
        return !!this.description?.trim();
    }

    get hasImageContent(): boolean {
        return !!this.image;
    }

    get hasRecommendationContent(): boolean {
        if (this.isPostingUnavailable) {
            return true;
        }

        const linkedTitle = this.isLinked ? this.posting?.title : null;
        const linkedDescription = this.isLinked ? this.posting?.description : null;

        return !!(this.title?.trim() || linkedTitle?.trim() || this.description?.trim() || linkedDescription?.trim() || this.posting);
    }

    get hasVisibleContent(): boolean {
        switch (this.type) {
            case CourseOfferingRecommendationComponentType.Title:
                return this.hasTitleContent;
            case CourseOfferingRecommendationComponentType.Description:
                return this.hasDescriptionContent;
            case CourseOfferingRecommendationComponentType.Image:
                return this.hasImageContent;
            case CourseOfferingRecommendationComponentType.Recommendation:
                return this.hasRecommendationContent;
            default:
                return true;
        }
    }

    get isVisibleInParticipantView(): boolean {
        if (!this.hasVisibleContent) {
            return false;
        }

        if (this.type !== CourseOfferingRecommendationComponentType.Recommendation) {
            return true;
        }

        return !this.isPostingUnavailable;
    }

    static filterParticipantVisibleComponents(components: CourseOfferingRecommendationItem[]): CourseOfferingRecommendationItem[] {
        return components.filter((component) => component.isVisibleInParticipantView);
    }

    private resolveAlignment(alignment: string | null | undefined): CourseOfferingRecommendationComponentAlignment {
        if (alignment === CourseOfferingRecommendationComponentAlignment.Left || alignment === CourseOfferingRecommendationComponentAlignment.Right) {
            return alignment;
        }

        return CourseOfferingRecommendationComponentAlignment.Center;
    }
}
